/**
 * BrainSchedulePage — View all agentic scheduled jobs for the selected tenant.
 *
 * Shows the live queue from the `scheduled_jobs` tenant table:
 *  - ETA-offset timeline entries (name = "timeline:jobId:ruleId")
 *  - Cron jobs  (trigger_type = 'cron')
 *  - One-時 triggers (trigger_type = 'once')
 *
 * Supports filtering by: domain, type (timeline/cron/one_time), status (pending/triggered/cancelled)
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CalendarClock, RefreshCw, X, Filter, Clock, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface ScheduledJob {
  id: string;
  name: string;
  skill: string;
  description: string;
  job_type: string | null;
  cron: string | null;
  run_at: string | null;
  timezone: string | null;
  is_active: boolean;
  run_count: number;
  max_runs: number | null;
  last_run_at: string | null;
  next_run_at: string | null;
  trigger_type: string;
  trigger_event: string | null;
  params: unknown;
  timeout_secs: number | null;
  starts_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

type StatusFilter = 'all' | 'pending' | 'triggered' | 'cancelled';
type TypeFilter = 'all' | 'timeline' | 'cron' | 'once';

function getJobStatus(job: ScheduledJob): 'pending' | 'triggered' | 'cancelled' | 'overdue' {
  if (!job.is_active && job.run_count === 0) return 'cancelled';
  if (job.run_count > 0) return 'triggered';
  if (job.run_at && new Date(job.run_at) < new Date()) return 'overdue';
  return 'pending';
}

function getRunAtLabel(runAt: string | null): { label: string; urgency: 'overdue' | 'soon' | 'normal' | 'future' } {
  if (!runAt) return { label: '—', urgency: 'normal' };
  const now = Date.now();
  const ts = new Date(runAt).getTime();
  const diffMs = ts - now;
  const diffHours = diffMs / 1000 / 3600;

  if (diffMs < 0) {
    const ago = Math.abs(diffMs) / 1000 / 3600;
    if (ago < 24) return { label: `${Math.round(ago)}h ago`, urgency: 'overdue' };
    return { label: `${Math.round(ago / 24)}d ago`, urgency: 'overdue' };
  }
  if (diffHours < 24) return { label: `in ${Math.round(diffHours)}h`, urgency: 'soon' };
  if (diffHours < 72) return { label: `in ${Math.round(diffHours / 24)}d`, urgency: 'normal' };
  return { label: new Date(runAt).toLocaleDateString(), urgency: 'future' };
}

function parseDomainFromSkill(skill: string): string {
  if (skill.includes('sea-import') || skill.includes('sea_import')) return 'sea-import';
  if (skill.includes('truck') || skill.includes('container')) return 'container-trucking';
  if (skill.includes('nvocc') || skill.includes('lcl')) return 'nvocc';
  return skill;
}

function parseRuleIdFromName(name: string): string {
  // "timeline:jobId:ruleId" → "ruleId"
  const parts = name.split(':');
  return parts.length >= 3 ? (parts.slice(2).join(':') || name) : name;
}

function parseJobIdFromName(name: string): string {
  const parts = name.split(':');
  return parts.length >= 2 ? (parts[1] ?? name) : name;
}

const STATUS_BADGE: Record<string, { label: string; className: string; icon: typeof Clock }> = {
  pending: { label: '等待中', className: 'bg-blue-100 text-blue-700', icon: Clock },
  overdue: { label: '已超时', className: 'bg-red-100 text-red-700', icon: AlertTriangle },
  triggered: { label: '已触发', className: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  cancelled: { label: '已取消', className: 'bg-gray-100 text-gray-500', icon: XCircle },
};

const DOMAIN_BADGE: Record<string, string> = {
  'sea-import': 'bg-indigo-100 text-indigo-700',
  'container-trucking': 'bg-amber-100 text-amber-700',
  nvocc: 'bg-purple-100 text-purple-700',
};

export default function BrainSchedulePage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTenantId = searchParams.get('tenantId') ?? '';
  const tenantName = searchParams.get('tenantName') ?? selectedTenantId;

  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchJobs = useCallback(async () => {
    if (!selectedTenantId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (typeFilter !== 'all') params.set('type', typeFilter === 'cron' ? 'cron' : typeFilter === 'once' ? 'once' : 'timeline');
      if (domainFilter !== 'all') params.set('domain', domainFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const res = await apiFetch<ScheduledJob[]>(
        `/platform/tenants/${selectedTenantId}/schedule?${params.toString()}`
      );
      setJobs(Array.isArray(res) ? res : []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId, typeFilter, statusFilter, domainFilter]);

  useEffect(() => {
    void fetchJobs();
  }, [fetchJobs]);

  const handleCancel = async (jobId: string) => {
    if (!selectedTenantId) return;
    setCancelling(jobId);
    try {
      await apiFetch(`/platform/tenants/${selectedTenantId}/schedule/${jobId}`, { method: 'DELETE' });
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, is_active: false } : j));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Cancel failed');
    } finally {
      setCancelling(null);
    }
  };

  const isTimeline = (job: ScheduledJob) => job.name.startsWith('timeline:');

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <CalendarClock className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-base font-semibold">{t('admin.brainSchedule', 'Brain 调度队列')}</h1>
            <p className="text-xs text-muted-foreground">
              {selectedTenantId ? `租户: ${tenantName}` : '请选择租户'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{jobs.length} 条记录</span>
          <Button variant="ghost" size="icon" onClick={() => void fetchJobs()} disabled={loading}>
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Tenant ID input (if not in URL) */}
      {!selectedTenantId && (
        <div className="px-6 py-3 border-b border-border bg-amber-50/50 flex items-center gap-3">
          <span className="text-xs text-amber-700">请输入租户 ID：</span>
          <Input
            placeholder="tenant-id (e.g. lch-logistics)"
            className="h-8 w-64 text-xs"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                const v = (e.target as HTMLInputElement).value.trim();
                if (v) setSearchParams({ tenantId: v });
              }
            }}
          />
          <span className="text-[10px] text-muted-foreground">按回车确认</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-border bg-muted/30 shrink-0">
        <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
        <Select value={typeFilter} onValueChange={v => setTypeFilter(v as TypeFilter)}>
          <SelectTrigger className="h-8 w-36 text-xs">
            <SelectValue placeholder="类型" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部类型</SelectItem>
            <SelectItem value="timeline">ETA 时间轴</SelectItem>
            <SelectItem value="cron">定时 Cron</SelectItem>
            <SelectItem value="once">单次触发</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="h-8 w-32 text-xs">
            <SelectValue placeholder="状态" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部状态</SelectItem>
            <SelectItem value="pending">等待中</SelectItem>
            <SelectItem value="triggered">已触发</SelectItem>
            <SelectItem value="cancelled">已取消</SelectItem>
          </SelectContent>
        </Select>

        <Select value={domainFilter} onValueChange={setDomainFilter}>
          <SelectTrigger className="h-8 w-40 text-xs">
            <SelectValue placeholder="业务域" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部业务域</SelectItem>
            <SelectItem value="sea-import">海运进口</SelectItem>
            <SelectItem value="container-trucking">集装箱拖车</SelectItem>
            <SelectItem value="nvocc">NVOCC/LCL</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-6 mt-3 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}

      {/* Not selected */}
      {!selectedTenantId && (
        <div className="flex flex-1 items-center justify-center text-muted-foreground text-sm">
          请先输入租户 ID
        </div>
      )}

      {/* Empty */}
      {selectedTenantId && !loading && jobs.length === 0 && !error && (
        <div className="flex flex-1 items-center justify-center text-muted-foreground text-sm">
          暂无匹配的调度任务
        </div>
      )}

      {/* Job list */}
      {jobs.length > 0 && (
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-xs text-muted-foreground w-28">业务域</th>
                <th className="px-4 py-2 text-left font-medium text-xs text-muted-foreground">描述 / 规则</th>
                <th className="px-4 py-2 text-left font-medium text-xs text-muted-foreground w-28">关联工作</th>
                <th className="px-4 py-2 text-left font-medium text-xs text-muted-foreground w-28">执行时间</th>
                <th className="px-4 py-2 text-left font-medium text-xs text-muted-foreground w-24">状态</th>
                <th className="px-4 py-2 text-left font-medium text-xs text-muted-foreground w-20">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {jobs.map(job => {
                const status = getJobStatus(job);
                const { label: runAtLabel, urgency } = getRunAtLabel(job.run_at);
                const domain = parseDomainFromSkill(job.skill ?? '');
                const StatusIcon = STATUS_BADGE[status]?.icon ?? Clock;
                const isExpanded = expandedId === job.id;
                const timeline = isTimeline(job);

                return (
                  <>
                    <tr
                      key={job.id}
                      className={cn(
                        'hover:bg-muted/30 cursor-pointer transition-colors',
                        status === 'cancelled' && 'opacity-50',
                        isExpanded && 'bg-muted/20'
                      )}
                      onClick={() => setExpandedId(isExpanded ? null : job.id)}
                    >
                      {/* Domain */}
                      <td className="px-4 py-2.5">
                        <Badge
                          variant="secondary"
                          className={cn('text-xs font-normal', DOMAIN_BADGE[domain] ?? 'bg-gray-100 text-gray-600')}
                        >
                          {domain}
                        </Badge>
                      </td>

                      {/* Description */}
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-xs leading-snug">
                          {job.description || (timeline ? parseRuleIdFromName(job.name) : job.name)}
                        </div>
                        {timeline && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            规则: {parseRuleIdFromName(job.name)}
                          </div>
                        )}
                      </td>

                      {/* Related job */}
                      <td className="px-4 py-2.5">
                        {timeline ? (
                          <span className="text-xs font-mono text-muted-foreground truncate max-w-[100px] block">
                            {parseJobIdFromName(job.name)}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {job.cron
                              ? `${job.cron}${job.timezone ? ` (${job.timezone})` : ''}`
                              : (job.trigger_event || '—')}
                          </span>
                        )}
                      </td>

                      {/* Run at */}
                      <td className="px-4 py-2.5">
                        <span
                          className={cn(
                            'text-xs font-medium',
                            urgency === 'overdue' && 'text-red-600',
                            urgency === 'soon' && 'text-amber-600',
                            urgency === 'normal' && 'text-foreground',
                            urgency === 'future' && 'text-muted-foreground'
                          )}
                        >
                          {runAtLabel}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-2.5">
                        <div className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium', STATUS_BADGE[status]?.className)}>
                          <StatusIcon className="h-3 w-3" />
                          {STATUS_BADGE[status]?.label ?? status}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                        {status === 'pending' || status === 'overdue' ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive"
                            disabled={cancelling === job.id}
                            onClick={() => void handleCancel(job.id)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                      </td>
                    </tr>

                    {/* Expanded params panel */}
                    {isExpanded && (
                      <tr key={`${job.id}-params`}>
                        <td colSpan={6} className="px-6 pb-3 pt-0 bg-muted/10">
                          <div className="rounded-md border border-border bg-background p-3">
                            <div className="text-[10px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                              参数 / Params
                            </div>
                            <pre className="text-[10px] text-foreground whitespace-pre-wrap leading-snug max-h-40 overflow-y-auto">
                              {JSON.stringify(job.params, null, 2)}
                            </pre>
                            <div className="mt-2 flex gap-4 text-[10px] text-muted-foreground">
                              <span>创建: {new Date(job.created_at).toLocaleString()}</span>
                              {job.last_run_at && <span>最后执行: {new Date(job.last_run_at).toLocaleString()}</span>}
                              {job.run_count > 0 && <span>已执行: {job.run_count}次</span>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
